import { HereLayer } from 'common/utils/MapMarker/types';

import { useMemo } from 'react';
import { useSelectedStationCode } from 'store/features/station';
import {
  useSelectedRouteStationCodes,
  useGetRouteStationsWithPrices
} from 'entities/stations';
import { useMappingStationsByCodes } from 'store/features/stations/selectors/allStationsMap';

import { svgToDataURL } from 'common/utils';
import { getStationCoordinates } from 'features/stations/StationLocations/hooks/useStationMarkersLayer';

import commonStationIcon from 'features/stations/StationLocations/assets/mapIcons/stations/common/default.png';

const getPopup = (name: string, value: string) => (
  `<svg width="118" height="72" viewBox="0 0 118 72" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_ddd_2012_6)">
    <rect x="16" y="13" width="86" height="30" rx="8" fill="white"/>
    <text fill="#30343B" xml:space="preserve" style="white-space: pre" font-family="sans-serif" font-size="10" font-weight="bold" letter-spacing="0.004em"><tspan x="24" y="31.418">${name}:</tspan></text>
    <text fill="#30343B" xml:space="preserve" style="white-space: pre" font-family="sans-serif" font-size="10" letter-spacing="0.004em"><tspan x="42" y="31.418">${value}</tspan></text>
    <path d="M73 43L45 43L46.2065 43C48.7863 43 51.2511 44.0348 53.0155 45.8587L57.2977 50.2853C58.2196 51.2382 59.7804 51.2382 60.7023 50.2853L64.9845 45.8587C66.7489 44.0348 69.2137 43 71.7935 43L73 43Z" fill="white"/>
    </g>
    <defs>
    <filter id="filter0_ddd_2012_6" x="0" y="0" width="118" height="72" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
    <feOffset dy="5"/>
    <feGaussianBlur stdDeviation="3"/>
    <feComposite in2="hardAlpha" operator="out"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0.388235 0 0 0 0 0.447059 0 0 0 0 0.509804 0 0 0 0.04 0"/>
    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2012_6"/>
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
    <feOffset dy="3"/>
    <feGaussianBlur stdDeviation="8"/>
    <feComposite in2="hardAlpha" operator="out"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0.388235 0 0 0 0 0.447059 0 0 0 0 0.509804 0 0 0 0.1 0"/>
    <feBlend mode="normal" in2="effect1_dropShadow_2012_6" result="effect2_dropShadow_2012_6"/>
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
    <feOffset dy="9"/>
    <feGaussianBlur stdDeviation="6"/>
    <feComposite in2="hardAlpha" operator="out"/>
    <feColorMatrix type="matrix" values="0 0 0 0 0.388235 0 0 0 0 0.447059 0 0 0 0 0.509804 0 0 0 0.12 0"/>
    <feBlend mode="normal" in2="effect2_dropShadow_2012_6" result="effect3_dropShadow_2012_6"/>
    <feBlend mode="normal" in="SourceGraphic" in2="effect3_dropShadow_2012_6" result="shape"/>
    </filter>
    </defs>
    </svg>`
);


export const useSelectedStationOnRoutePricesLayer = () => {
  const selectedStationCode = useSelectedStationCode();
  const selectedRouteStationCodes = useSelectedRouteStationCodes();
  const selectedRouteStations = useMappingStationsByCodes(selectedRouteStationCodes);  
  const getRouteStationsWithPrices = useGetRouteStationsWithPrices();

  const selectedStationOnRoutePricesLayer = useMemo((): HereLayer => ({
    data: selectedRouteStations,
    name: 'selected-station-prices-on-route',
    type: 'marker',
    getCoordinates: getStationCoordinates({ selectedStationCode }),
    getIcon: ({ data }: any) => {
      const ratio = 2.5;

      const stationWithPrices = getRouteStationsWithPrices(data.code);

      if (stationWithPrices && stationWithPrices.services.length) {
        const { name, price: { amount, currency } } = stationWithPrices.services[0] as any;
        return ({
          url: svgToDataURL(getPopup(name, `${amount} ${currency}`)),
          height: 72 * ratio,
          width: 118 * ratio,
          anchorY: 118 * 1.85,
          anchorX: 104 * 1.45,
          id: `${data.code}-selected-station-price`
        });
      }

      return ({
        url: commonStationIcon,
        height: 1,
        width: 1,
        anchorY: 0,
        id: '1'
      });
    },
  }), [
    selectedRouteStations,
    selectedStationCode,
    getRouteStationsWithPrices
  ]);

  return selectedStationOnRoutePricesLayer;
};
